export const BUYER_TYPES = [
  "Developer",
  "Landowner",
  "Planning consultant / planning professional",
  "Environmental / contaminated-land consultant",
  "Property due-diligence adviser",
  "Contractor",
  "Lawyer / legal adviser",
  "Lender / insurer",
  "Council / public-sector team",
  "Other",
];

export const SITE_TYPES = [
  "Brownfield development site",
  "Former industrial site",
  "Landfill / former landfill",
  "Fire-training / AFFF risk site",
  "Airport / airfield-related site",
  "Wastewater / water-related site",
  "Commercial property",
  "Public-sector land",
  "Unknown / not sure",
];

export const CURRENT_CONCERNS = [
  "Planning concern",
  "Acquisition due diligence",
  "Sale / transaction risk",
  "Construction risk",
  "Lender / insurer concern",
  "Council / enforcement issue",
  "Legal / recovery question",
  "Not sure yet",
];

export const EXISTING_REPORTS = ["Yes", "No", "Not sure"];

export const PORTFOLIO_SITE_COUNTS = ["2–5", "6–15", "16–50", "50+", "Not sure yet"];
export const PORTFOLIO_TYPES = [
  "Residential / housing land",
  "Mixed-use regeneration",
  "Logistics / industrial",
  "Commercial",
  "Strategic land / options",
  "Public-sector estate",
  "Mixed portfolio",
  "Other",
];
export const PORTFOLIO_TIMELINES = [
  "Immediate (0–4 weeks)",
  "Next quarter",
  "Next 6–12 months",
  "Ongoing acquisition programme",
  "Exploratory",
];

// Evidence-weighted triage. major: always a major trigger.
// conditional: major ONLY when the follow-up confirms PFAS-specific evidence.
export const TRIAGE_QUESTIONS = [
  { id: "q1",  q: "Has the site, or nearby land, had historic or current industrial or manufacturing use?",
    examples: "Plating, textiles, paper, coatings, chemicals, engineering works, depots or factories.",
    impact: "Generic industrial history is a contextual indicator. It becomes a major trigger only where the activity is specifically PFAS-relevant.",
    conditional: { key: "q1_pfas_specific",
      label: "Does the evidence point to a specifically PFAS-relevant process?",
      hint: "e.g. metal / chrome plating, fluoropolymer or fluorochemical manufacture, textile / paper / leather treatment with fluorinated coatings, firefighting-equipment manufacture or servicing." } },
  { id: "q2",  q: "Is there known or probable AFFF firefighting-foam use, a fire-training area, an airport / airfield with firefighting activity, or a firefighting facility on or near the site?",
    impact: "AFFF is a well-documented PFAS source. Fire-training and airfield firefighting activity are among the strongest PFAS source indicators.", major: true },
  { id: "q3",  q: "Is there historic or current landfill, waste disposal or waste-transfer history on or near the site?",
    impact: "Landfill history is a moderate contextual indicator. Its PFAS relevance depends on what was received and the pathways present." },
  { id: "q4",  q: "Is there wastewater treatment, sewage discharge or sewage-sludge-to-land history nearby?",
    impact: "Wastewater-related pathways are a moderate contextual indicator that may warrant PFAS-specific consideration." },
  { id: "q5",  q: "Is there a clear groundwater or surface-water migration pathway (streams, rivers, ditches, culverts, drains or shallow groundwater) linking the site to potential source areas?",
    impact: "Pathways matter in combination with a plausible source and receptor. A clear pathway alongside a credible source is a major indicator; water alone does not establish PFAS risk.", major: true },
  { id: "q6",  q: "Is there made ground or imported fill of unknown origin on the site?",
    impact: "Uncertain fill introduces an evidence gap that can affect construction, disposal, due diligence and funding." },
  { id: "q7",  q: "Is the site close to known contaminated, remediated, notified or historically industrial land?",
    impact: "General proximity is a contextual indicator. It becomes a major trigger only where there is PFAS-specific evidence nearby.",
    conditional: { key: "q7_pfas_specific",
      label: "Is there a known PFAS investigation, an identified PFAS source or other credible PFAS-specific evidence nearby?",
      hint: "e.g. a published PFAS investigation, regulatory PFAS sampling, a known AFFF source or PFAS-related enforcement in the area." } },
  { id: "q8",  q: "Are there planning conditions, planning comments or reports referencing contamination?",
    impact: "Unresolved contamination matters can delay planning condition discharge, funding, construction start, sale or occupation." },
  { id: "q9",  q: "Does an existing Phase 1 desk study or environmental report fail to specifically address PFAS?",
    impact: "Conventional desk studies may not answer PFAS-specific source, pathway and evidence-gap questions." },
  { id: "q10", q: "Has a lender, insurer, buyer, regulator, council, planning officer or other relevant professional raised a PFAS-specific question or concern?",
    impact: "A PFAS-specific question from a decision-maker is a strong signal that evidence will be required before funding, insurance, transaction or planning can progress.", major: true },
  { id: "q11", q: "Is there uncertainty over historic owners, operators or site activities?",
    impact: "Unclear historic operator and ownership information limits evidence assembly for later legal, regulatory, insurance or recovery assessment." },
  { id: "q12", q: "Are you under pressure to buy, build, fund, insure, sell or commit capital before PFAS screening has been completed?",
    impact: "Committing before screening narrows options and can increase later cost if evidence emerges." },
];

export const MAJOR_TRIGGER_LABELS = {
  q1: "PFAS-relevant industrial process",
  q2: "AFFF / fire-training / airfield firefighting source",
  q5: "Groundwater / surface-water pathway with plausible source",
  q7: "Known PFAS investigation or source nearby",
  q10: "PFAS-specific question already raised",
};

export const PAIN_POINTS = [
  { t: "Planning condition cannot be discharged", d: "Contamination planning conditions can stall commencement and drawdown." },
  { t: "Remediation cost was not priced into the deal", d: "Late discovery of PFAS or brownfield exposure changes deal economics." },
  { t: "Funding delayed by environmental uncertainty", d: "Lenders and internal credit committees stall on unresolved risk." },
  { t: "Insurance cover restricted or questioned", d: "Insurers may exclude PFAS, restrict cover or query the site." },
  { t: "Buyer renegotiates or walks away", d: "Environmental risk found late costs price, terms — or the deal." },
  { t: "Contamination is discovered too late", d: "Evidence that surfaces after commitment leaves the fewest options." },
  { t: "Your existing desk study does not clearly address PFAS", d: "PFAS may require more specific source and pathway consideration than conventional contamination screening provides." },
  { t: "Historic operator evidence is thin", d: "Weak ownership and operator information limits later legal, insurance or recovery assessment." },
];

export const SOURCE_REVIEW_AREAS = [
  "AFFF and firefighting-foam use",
  "Fire-training areas",
  "Airports and airfields",
  "Firefighting facilities",
  "Relevant industrial processes",
  "Neighbouring historic land uses",
  "Surface-water and groundwater pathways",
  "Drainage / wastewater pathways where relevant",
  "Known PFAS investigations",
  "Emerging regulatory or local evidence",
];

export const CONVENTIONAL_DD = [
  "General site history and contamination context",
  "Historic land-use assessment",
  "Conventional source-pathway-receptor assessment",
  "Planning-related contaminated-land review",
  "Recommendations for further investigation where appropriate",
];

export const PFAS_LAYER = [
  "PFAS-specific source screening",
  "Targeted review of activities associated with potential PFAS use",
  "Off-site PFAS source intelligence",
  "PFAS-specific pathway considerations",
  "Review of emerging PFAS evidence and known investigations where available",
  "Identification of PFAS evidence gaps",
  "Decision-focused Red / Amber / Green assessment",
  "Recommended PFAS-specific next steps",
];

export const DELIVERABLES = [
  "Executive summary",
  "Red / Amber / Green initial risk view",
  "PFAS source activity review",
  "Site and surrounding land-use review",
  "Source-pathway-receptor considerations",
  "Off-site migration considerations",
  "Known / emerging evidence where relevant",
  "Planning flags",
  "Transaction flags",
  "Lender / insurer considerations where relevant",
  "Evidence gaps",
  "Recommended next steps",
  "Scope and limitations",
];

export const HOW_IT_WORKS = [
  { n: "01", t: "Brief", d: "Site address, boundary, proposed use, planning reference and any existing reports." },
  { n: "02", t: "Source review", d: "PFAS-relevant source activity review on and beyond the site boundary, weighted by the strength of the available evidence." },
  { n: "03", t: "Pathway & evidence", d: "Source-pathway-receptor considerations, off-site influences, known or emerging evidence and the gaps that remain." },
  { n: "04", t: "Decision view", d: "Red / Amber / Green preliminary view, commercial flags and PFAS-specific recommended next steps — within 5–7 working days." },
];

export const SAMPLE_CARDS = [
  { t: "Executive RAG Summary", d: "One-page preliminary Red / Amber / Green view with the reasoning behind it." },
  { t: "Potential PFAS Source Map", d: "Illustrative plan of on-site and off-site potential source areas relative to the boundary." },
  { t: "Source–Pathway–Receptor Review", d: "How potential sources, pathways and receptors interact for the proposed use." },
  { t: "Evidence Gaps", d: "What is not yet known, and which gaps matter most for the decision in front of you." },
  { t: "Planning / Transaction Risk Flags", d: "Where PFAS uncertainty may surface in planning, funding, insurance or sale." },
  { t: "Recommended Next Move", d: "PFAS-specific next steps — proportionate to the evidence, not a default to full investigation." },
];

export const PRIMARY_SECTORS = [
  { t: "Developers", d: "Screen PFAS source risk before acquisition, planning submission, funding or contractor procurement locks in cost." },
  { t: "Landowners", d: "Understand PFAS-relevant history and evidence gaps before marketing, optioning or promoting land." },
  { t: "Planning professionals", d: "Anticipate PFAS-specific questions on contamination conditions and support proportionate responses." },
  { t: "Environmental / property advisers", d: "Add a PFAS-specific intelligence layer alongside Phase 1 work, transaction due diligence or portfolio review." },
];

export const SECONDARY_SECTORS = [
  { t: "Legal", d: "Historic operator and ownership intelligence and evidence collation to support advice on potentially responsible parties." },
  { t: "Insurance", d: "Source and pathway context where PFAS exclusions, restrictions or underwriting questions arise." },
  { t: "Lending", d: "Preliminary PFAS risk view to inform environmental conditions on funding decisions." },
  { t: "Public sector",
    h: "Supporting public-sector understanding of emerging PFAS risks, source information and evidence gaps.",
    d: "Desk-based source intelligence, historic land-use research, evidence collation, GIS / historic land-use intelligence and emerging PFAS case monitoring. PFAS Risk Intelligence does not replace statutory contaminated-land functions, regulatory decision-making or legal advice." },
];

export const RISK_WATCH_SOURCES = [
  "Environment Agency", "DEFRA", "UKHSA", "Drinking Water Inspectorate",
  "British Geological Survey", "CL:AIRE", "Local authorities",
  "Planning decisions", "Court / enforcement developments",
  "Technical publications", "Significant UK PFAS investigations",
];

export const RELEVANCE_TAGS = [
  "Planning", "Acquisition", "Development", "Lending",
  "Insurance", "Remediation", "Legal / recovery",
];

export const FUTURE_CAPABILITIES = [
  "PFAS source-site intelligence",
  "Known case tracking",
  "Historic AFFF / fire-training intelligence",
  "Airports and airfields",
  "Relevant industrial source activities",
  "Geographic intelligence",
  "Portfolio screening",
  "Regulatory and planning developments",
];

export const DISCLAIMER_TEXT =
  "PFAS Risk Intelligence provides desk-based risk intelligence and strategy coordination. Our outputs are preliminary information tools only. They do not replace accredited laboratory testing, formal contaminated land assessment, legal advice, regulatory approval, expert evidence or remediation design. Our work does not confirm the presence or absence of PFAS and does not determine legal liability. Where risk is identified or cannot be ruled out, appropriate specialist investigation, laboratory testing, legal advice and professional contaminated land assessment should be obtained before making acquisition, planning, construction, remediation, enforcement, insurance, recovery or litigation decisions.";

export const FAQS = [
  { q: "Is PFAS only a water company or government problem?",
    a: "No. PFAS can affect land, groundwater, planning, construction, insurance, waste disposal, transactions and long-term liability. Developers, landowners, planning professionals, advisers, lenders, insurers and legal teams may all be affected." },
  { q: "Will my Phase 1 desk study already cover this?",
    a: "It may, in part. Conventional Phase 1 reports address general contamination context and may not provide a PFAS-specific source, pathway and evidence-gap review — particularly where potential risk is linked to firefighting foam, fire-training, airfields, specific industrial processes or off-site sources. The Risk Screen complements that work; it does not replace it." },
  { q: "Do you replace my contaminated-land consultant?",
    a: "No. We provide a specialist desk-based PFAS intelligence layer that helps determine whether further professional investigation may be justified. Phase 1 desk studies, Phase 2 investigations, laboratory testing, regulatory assessment and professional environmental advice remain essential." },
  { q: "Do I need expensive testing straight away?",
    a: "Not necessarily. A desk-based PFAS Brownfield Risk Screen can help decide whether targeted sampling, laboratory testing, legal review or further investigation is justified — and where to focus it." },
  { q: "Can PFAS affect a site that was never used for manufacturing?",
    a: "Yes. PFAS can originate beyond the site boundary and move through groundwater, surface water, drainage and wastewater pathways. That is why we review both on-site history and surrounding source activity and pathways." },
  { q: "Is every landfill or industrial estate a PFAS risk?",
    a: "No. We use an evidence-weighted approach. Specific source evidence — known or probable AFFF use, fire-training grounds, airfield firefighting activity, PFAS-relevant industrial processes or known PFAS investigations nearby — carries more weight than broad historic land-use labels." },
  { q: "If PFAS is present, is the site finished?",
    a: "Not necessarily. The key questions are source, pathway, receptor, proposed use, concentration, planning context, management options and commercial implications." },
  { q: "Can you confirm whether PFAS is present?",
    a: "Not without laboratory testing. The Risk Screen is a preliminary desk-based review. Where risk is identified or cannot be ruled out, we recommend appropriate specialist investigation." },
  { q: "Do you determine legal liability?",
    a: "No. We do not determine legal liability or identify a legally liable party. Where relevant, we can help assemble historic ownership, operator and source information about potentially responsible parties to support further legal, regulatory, insurance or recovery assessment by appropriate advisers." },
  { q: "What if PFAS rules change?",
    a: "PFAS regulation is evolving. Our work is not limited to current thresholds — we help clients understand potential sources, pathways, evidence gaps, planning risk, insurance concern and commercial exposure so decisions hold up as the regulatory position develops." },
  { q: "What do I need to provide?",
    a: "Site address, boundary plan, proposed use, planning reference if available and any existing environmental or planning reports." },
  { q: "How quickly can we get started?",
    a: "The PFAS Brownfield Risk Screen is normally delivered within 5–7 working days once we have the required site information." },
  { q: "What is not included in the Risk Screen?",
    a: "Intrusive investigation, laboratory testing, legal advice, regulatory approval, remediation design or expert witness evidence — unless separately agreed through specialist support." },
];
